import {
  Button,
  MenuItem,
  FormControl,
  Grid,
  InputLabel,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { produce } from "immer";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { cacheService, userService } from "../../../Core/Api/services";
import moment from "moment";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { DbPlatformAlertInfos } from "../../../Core/Api/Types/db-platform-alert-infos";
import { PlatformAlertSeverity } from "../../../Core/Api/Types/platform-alert-severity";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
  confirm: boolean;
};

export type ModalAddAlertElementProps = {
  onClose?: (props: CloseClbProps) => void;
  onConfirm?: (alert: DbPlatformAlertInfos, id: number) => void;
};
export type ModalAddAlertProps = CommonModalProps<ModalAddAlertElementProps>;

const ModalAddAlert = React.forwardRef<any, ModalAddAlertProps>((props, ref) => {
  const { closeModal, onClose, onConfirm, ...modalProps } = props;
  const [newAlert, setNewAlert] = React.useState<DbPlatformAlertInfos>(
    new DbPlatformAlertInfos({
      creationDate: moment().toDate(),
      userId: cacheService.GetUserInfo()?.id,
      userName: cacheService.GetUserInfo()?.name,
    })
  );
  const enqueueSnackbar = useCustomSnackbar();
  const { t } = useTranslation();

  const handleCancelClick = () => {
    onClose && onClose({ confirm: false });
    closeModal();
  };

  const handleConfirmClick = () => {
    if (newAlert) {
      userService.CreateAlert(newAlert).then(function (response) {
        if (response.success && response.objectId) {
          enqueueSnackbar(t("alert:success.newAlertCreated"), "success");
          onClose && onClose({ confirm: true });
          onConfirm && onConfirm(newAlert, response.objectId);
          closeModal();
        } else onClose && onClose({ confirm: false });
      });
    }
  };

  return (
    <Modal
      {...modalProps}
      title={t("modal:title.newAlert")}
      reference={ref}
      closeModal={handleCancelClick}
      width={680}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmClick}
            disabled={
              newAlert.severity === null ||
              newAlert.subject === null ||
              newAlert.message === null ||
              newAlert.startDate === null ||
              newAlert.endDate === null
            }
          >
            {t("common:create")}
          </Button>
        </Stack>
      }
    >
      <Typography variant="h5" mb={2}>
        {t("common:generalInformation")}
      </Typography>
      <Grid container spacing={2} mb={5}>
        <Grid item md={12}>
          <FormControl sx={{ width: "100%" }}>
            <InputLabel shrink htmlFor="subject">
              {t("field:label.subject")}
            </InputLabel>
            <OutlinedInput
              id="subject"
              value={newAlert.subject || ""}
              onChange={(e) => {
                setNewAlert(
                  produce((draft) => {
                    draft!.subject = e.target.value;
                  })
                );
              }}
            />
          </FormControl>
        </Grid>
        <Grid item md={12}>
          <FormControl sx={{ width: "100%" }}>
            <InputLabel shrink htmlFor="message">
              {t("common:message")}
            </InputLabel>
            <OutlinedInput
              id="message"
              value={newAlert.message || ""}
              multiline
              rows={3}
              onChange={(e) => {
                setNewAlert(
                  produce((draft) => {
                    draft!.message = e.target.value;
                  })
                );
              }}
            />
          </FormControl>
        </Grid>
        <Grid item md={6}>
          <FormControl sx={{ width: "100%" }}>
            <InputLabel shrink htmlFor="severity">
              {t("common:severity")}
            </InputLabel>
            <Select
              displayEmpty
              IconComponent={ExpandMoreRoundedIcon}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              value={newAlert.severity || ""}
              onChange={(e) =>
                setNewAlert(
                  produce((draft) => {
                    draft!.severity = PlatformAlertSeverity[e.target.value];
                  })
                )
              }
            >
              <MenuItem value="" selected>
                {t("menu:title.selectSeverity")}
              </MenuItem>
              {Object.keys(PlatformAlertSeverity).map((severity) => (
                <MenuItem value={severity} key={severity}>
                  {severity}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Typography variant="h5" mb={2}>
        {t("common:date")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item md={6}>
          <FormControl sx={{ width: "100%" }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={t("common:startingDate")}
                value={newAlert?.startDate}
                format={GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS}
                onChange={(e) => {
                  setNewAlert(
                    produce((draft) => {
                      draft!.startDate = e;
                    })
                  );
                }}
              />
            </LocalizationProvider>
          </FormControl>
        </Grid>
        <Grid item md={6}>
          <FormControl sx={{ width: "100%" }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={t("common:endingDate")}
                value={newAlert?.endDate}
                format={GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS}
                onChange={(e) => {
                  setNewAlert(
                    produce((draft) => {
                      draft!.endDate = e;
                    })
                  );
                }}
              />
            </LocalizationProvider>
          </FormControl>
        </Grid>
      </Grid>
    </Modal>
  );
});

export default ModalAddAlert;
